import React, { useEffect, useState } from "react";
import { Leaf, AlertTriangle, Clock, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { num } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Badge } from "@/components/common";

const STATUS = {
  fresh: { label: "Segar", cls: "bg-emerald-100 text-emerald-700" },
  warning: { label: "Mendekati layu", cls: "bg-amber-100 text-amber-700" },
  expired: { label: "Kadaluarsa", cls: "bg-rose-100 text-rose-700" },
};

export default function Freshness() {
  const { branch, isOwner } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!branch) return;
    setLoading(true);
    try { setData(await api.get("/freshness", { branch_id: branch.id })); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [branch]);

  const setThreshold = async () => {
    const w = window.prompt("Peringatan mendekati layu pada usia (hari)?", String(data?.warn_days || 10));
    if (!w) return;
    const e = window.prompt("Dianggap kadaluarsa pada usia (hari)?", String(data?.expire_days || 14));
    if (!e) return;
    try { await api.patch("/freshness/config", { warn_days: parseInt(w, 10), expire_days: parseInt(e, 10) }); toast.success("Ambang diperbarui"); load(); }
    catch (err) { toast.error(err.message); }
  };

  if (loading) return <Loading label="Memuat kesegaran bunga..." />;
  const sm = data?.summary || {};

  return (
    <>
      <PageHeader title="Kesegaran Bunga" subtitle={branch?.name} icon={Leaf}
        actions={isOwner && <Button variant="secondary" onClick={setThreshold} data-testid="freshness-threshold-btn"><CalendarClock size={16} /> Peringatan {data?.warn_days}h / Kadaluarsa {data?.expire_days}h</Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[["Segar", sm.fresh ?? 0, "text-emerald-600"], ["Mendekati layu", sm.warning ?? 0, "text-amber-600"], ["Kadaluarsa", sm.expired ?? 0, "text-rose-600"], ["Item berisiko", sm.items_at_risk ?? 0, "text-slate-700"]].map(([l, v, c]) => (
          <Card key={l} className="py-3" data-testid={`freshness-stat-${l}`}><div className="text-xs text-slate-400">{l}</div><div className={`text-2xl font-bold ${c}`}>{v}</div></Card>
        ))}
      </div>

      <div className="text-sm text-slate-500 mb-3 flex items-center gap-1.5"><Clock size={15} /> Usia dihitung otomatis dari tanggal masuk (FIFO). Batch tertua tampil lebih dulu.</div>

      {!data?.rows?.length ? (
        <EmptyState icon={Leaf} title="Belum ada batch bunga segar" message="Batch bunga akan muncul setelah barang masuk / stok bunga dicatat." />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
              <th className="py-3 px-4 font-medium">Bunga</th><th className="py-3 px-4 font-medium">Batch</th><th className="py-3 px-4 font-medium text-right">Sisa</th>
              <th className="py-3 px-4 font-medium">Tanggal Masuk</th><th className="py-3 px-4 font-medium text-right">Usia</th><th className="py-3 px-4 font-medium">Status</th>
            </tr></thead>
            <tbody>
              {data.rows.map((r, i) => {
                const st = STATUS[r.status] || STATUS.fresh;
                return (
                  <tr key={i} className="border-b border-slate-50" data-testid={`freshness-row-${r.item_id}-${i}`}>
                    <td className="py-3 px-4"><div className="font-medium text-slate-800">{r.name}</div><div className="text-xs text-slate-400">{r.supplier}</div></td>
                    <td className="py-3 px-4 text-slate-500">{r.batch_no}</td>
                    <td className="py-3 px-4 text-right">{num(r.qty_remaining)} {r.unit}</td>
                    <td className="py-3 px-4 text-slate-500">{(r.arrival_date || "").toString().slice(0, 10) || "-"}</td>
                    <td className="py-3 px-4 text-right">{r.age_days} hari</td>
                    <td className="py-3 px-4"><Badge className={`${st.cls} flex items-center gap-1 w-fit`}>{r.status !== "fresh" && <AlertTriangle size={11} />}{st.label}{r.status !== "expired" && r.days_left >= 0 ? ` · ${r.days_left}h lagi` : ""}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
